import { IsVarEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { ALL_VALUE, INVALID_VALUE } from '../../Common/Constants/DefaultValues'
import { IndividualTelItem } from '../../Common/Types/Types'
import { RecordingIntegrations } from '../../Common/Constants/Recordings'
import { SERVER_NONE_VALUE } from '../Constants'
import { PartitionValues } from '../Types'

/**
 * The TelSys and partition identifiers a call recording integrations lookup is scoped to.
 */
export interface CallRecordingScope {
    readonly telSysID: number
    readonly partitionID: number
}

/**
 * Resolves which TelSys the reporting tab describes, preferring the attached endpoint's telephony
 * server over the one granted by the user's permissions scope.
 *
 * @param endpointTelSysValue The TelSys identifier of the attached endpoint.
 * @param telSysValue The TelSys identifier selected in the permissions form.
 *
 * @returns The TelSys identifier to load, or `null` when neither selection names a single one.
 */
export const resolveTelSysToUse = (endpointTelSysValue: number, telSysValue: number): Nullable<number> => {
    if (endpointTelSysValue && endpointTelSysValue !== ALL_VALUE && endpointTelSysValue !== INVALID_VALUE)
        return endpointTelSysValue

    if (telSysValue && telSysValue !== ALL_VALUE && telSysValue !== INVALID_VALUE) return telSysValue

    return null
}

/**
 * Resolves the TelSys and partition identifiers to request call recording integrations with.
 *
 * The form holds the selected TelSys as a plain identifier, while the corresponding entity is
 * fetched separately and therefore arrives later. A selection naming a single TelSys must never
 * fall back to {@link ALL_VALUE} during that window: the API rejects an "all TelSyss" lookup for
 * any user scoped to a single telephony server, which surfaces as a spurious permission error.
 *
 * The partition entity stays unresolved for legitimate reasons - it is only fetched for a single
 * concrete partition - so it always falls back to {@link ALL_VALUE}.
 *
 * @param endpointTelSysValue The TelSys identifier of the attached endpoint.
 * @param telSysValue The TelSys identifier selected in the permissions form.
 * @param telSys The resolved TelSys entity, absent until its request completes.
 * @param partition The resolved partition entity, absent unless a single partition is selected.
 *
 * @returns The identifiers to request with, or `null` while an expected TelSys is still resolving.
 */
export const resolveCallRecordingScope = (
    endpointTelSysValue: number,
    telSysValue: number,
    telSys: Nullable<IndividualTelItem>,
    partition: Nullable<PartitionValues>
): Nullable<CallRecordingScope> => {
    const expectedTelSysID: Nullable<number> = resolveTelSysToUse(endpointTelSysValue, telSysValue)

    if (isVarNotEmpty(expectedTelSysID) && IsVarEmpty(telSys)) return null

    return {
        telSysID: telSys?.TelSysID ?? ALL_VALUE,
        partitionID: partition?.PartIDInTelSys ?? ALL_VALUE
    }
}

/**
 * Resolves the call recording integration to preselect for a new user, preferring the TelSys
 * default over the partition default.
 *
 * With neither default set this returns {@link SERVER_NONE_VALUE}, which is the value the
 * "None" option of the call recording selector carries and which the server mapper translates to
 * the None integration ID. Any other placeholder would be submitted verbatim and rejected by the
 * server as an unknown call recording integration.
 *
 * @param telSys The resolved TelSys entity, carrying its call recording default.
 * @param partition The resolved partition entity, carrying its call recording default.
 *
 * @returns The call recording integration ID to preselect, or {@link SERVER_NONE_VALUE}.
 */
export const resolveCallRecordingDefault = (
    telSys: Nullable<IndividualTelItem>,
    partition: Nullable<PartitionValues>
): string => {
    const telSysDefault: Definable<string> = telSys?.CallRecIntIDDefault
    if (telSysDefault && telSysDefault !== RecordingIntegrations.NONE) return telSysDefault

    const partitionDefault: Definable<string> = partition?.CallRecordingIDDefault
    if (partitionDefault && partitionDefault !== RecordingIntegrations.NONE) return partitionDefault

    return SERVER_NONE_VALUE
}
